import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { authService, LoginCredentials, AuthUser } from '../services/auth-service';
import { apiClient } from '../services/api-client';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: {
    id: number;
    email: string;
    fullName: string;
    role: 'user' | 'provider' | 'admin';
  } | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const USER_STORAGE_KEY = 'admin_user_data';

type AdminUserView = {
  id: number;
  email: string;
  fullName: string;
  role: 'admin';
};

const toAdminView = (u: {
  id: number;
  email: string;
  name?: string;
  fullName?: string;
  role: string;
}): AdminUserView | null => {
  if (u.role !== 'admin') return null;
  return {
    id: u.id,
    email: u.email,
    fullName: u.fullName || u.name || '',
    role: 'admin',
  };
};

/** Read cached user for loading UX only — never treat as authorization. */
const getCachedUserHint = (): AdminUserView | null => {
  try {
    const stored = sessionStorage.getItem(USER_STORAGE_KEY);
    if (!stored) {
      localStorage.removeItem(USER_STORAGE_KEY);
      return null;
    }
    const parsed = JSON.parse(stored) as AuthUser;
    return toAdminView(parsed);
  } catch {
    return null;
  }
};

const saveUserToStorage = (user: AdminUserView | null) => {
  try {
    if (user?.role === 'admin') {
      sessionStorage.setItem(
        USER_STORAGE_KEY,
        JSON.stringify({
          id: user.id,
          email: user.email,
          name: user.fullName,
          role: 'admin',
        })
      );
    } else {
      sessionStorage.removeItem(USER_STORAGE_KEY);
    }
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_token');
  } catch {
    /* ignore */
  }
};

const clearAuthState = () => {
  apiClient.setToken(null);
  apiClient.clearSessionSecrets();
  apiClient.preferCookieSession();
  saveUserToStorage(null);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Cached hint only — isAuthenticated stays false until server confirms admin.
  const [user, setUser] = useState<AdminUserView | null>(getCachedUserHint());

  const refreshUser = useCallback(async () => {
    try {
      const u = await authService.verifyAdminSession();
      const userData = toAdminView(u);
      if (!userData) {
        clearAuthState();
        setUser(null);
        setIsAuthenticated(false);
        return;
      }
      setUser(userData);
      saveUserToStorage(userData);
      setIsAuthenticated(true);
    } catch {
      clearAuthState();
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Cookie / server session is the only authority — never elevate from localStorage role alone.
        await refreshUser();
      } catch {
        clearAuthState();
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };
    void checkAuth();
  }, [refreshUser]);

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await authService.login(credentials);
      const userData = toAdminView(response.user);
      if (!userData) {
        clearAuthState();
        setUser(null);
        setIsAuthenticated(false);
        throw new Error('Access denied. Admin role required.');
      }

      setUser(userData);
      saveUserToStorage(userData);
      setIsAuthenticated(true);
    } catch (error) {
      clearAuthState();
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    }
  };

  const logout = () => {
    void authService.logout();
    clearAuthState();
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
